import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getDashboardSpecialMeals, getDashboardSpecialMealsSelector} from '@/store/organization-dashboard';
//mui
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel} from '@mui/material';
//constants
import {
  BIDDER_ID,
  BID_ID_CAMEL,
  DOWNLOAD_CSV,
  EMAIL,
  FIRST_NAME,
  FIRST_NAME_CAMEL,
  HASH,
  LAST_NAME,
  LAST_NAME_CAMEL,
  NAME,
  PHONE_NUMBER,
  SPECIAL_MEALS,
  defaultPage,
  defaultPageSize,
} from '@/utils/constants';
//helpers
import {downloadFile, getErrorMessage} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
//services
import {OrganizationDashboardService} from '@/utils/services';
//enums
import {STATISTIC} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticSpecialMealsStyle} from './StatisticSpecialMeals.styles';

const StatisticSpecialMeals = () => {
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [searchName, setSearchName] = useState('');

  const [sortColumn, setSortColumn] = useState<string>();
  const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const specialMeals = useAppSelector(getDashboardSpecialMealsSelector);

  const {data, pageSize, totalCount} = specialMeals;

  useEffect(() => {
    if (currentEvent) getSpecialMeals(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc]);

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
    setIsOrderDesc(isAsc);
    setSortColumn(newSortColumn);
  };

  const getSpecialMeals = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        eventId: currentEvent!.id,
        page: currentPage,
        sortColumn,
        isOrderDesc,
        searchName: value,
      };

      await dispatch(getDashboardSpecialMeals(reqBody)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handleDownloadSvc = async () => {
    if (currentEvent) {
      const file = await OrganizationDashboardService.getDashboardSpecialMealsCsv(currentEvent.id);
      downloadFile(file.data, STATISTIC.SPECIAL_MEALS);
    }
  };

  const handleSearchChange = (value: string) => {
    setCurrentPage(defaultPage);
    setSearchName(value);
  };

  const handlePaginationChange = (step: number) => {
    if (isOrderDesc) setIsOrderDesc(false);
    if (sortColumn?.length) setSortColumn('');

    setCurrentPage(step);
  };

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
  };

  useEffect(() => {
    if (currentEvent) getSpecialMeals(searchName);
  }, [currentEvent, currentPage, sortColumn, isOrderDesc, searchName]);

  return (
    <Box sx={statisticSpecialMealsStyle.wrapper}>
      <SearchHeader
        page={currentPage}
        items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
        total={totalCount}
        onPressEnter={handlePressEnter}
        onSearchChange={handleSearchChange}
        actionButton={
          <Button
            disabled={!totalCount}
            onClick={handleDownloadSvc}
            title={DOWNLOAD_CSV}
            startIcon={<ImportFileIcon />}
            sx={statisticSpecialMealsStyle.searchButton}
          />
        }
      />

      {isDataLoaded ? (
        totalCount ? (
          <Box sx={statisticSpecialMealsStyle.tableContent}>
            <TableContainer sx={statisticSpecialMealsStyle.container}>
              <Table sx={statisticSpecialMealsStyle.table}>
                <TableHead>
                  <TableRow sx={statisticSpecialMealsStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(BID_ID_CAMEL)} IconComponent={UpDownIcon}>
                        {BIDDER_ID}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(FIRST_NAME_CAMEL)} IconComponent={UpDownIcon}>
                        {NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>{EMAIL}</TableCell>

                    <TableCell>{PHONE_NUMBER}</TableCell>

                    <TableCell>{SPECIAL_MEALS}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.attendeeId} sx={statisticSpecialMealsStyle.tableRow}>
                      <TableCell>
                        {HASH}
                        {row.attendeeId}
                      </TableCell>
                      <TableCell>{`${row.firstName} ${row.lastName}`}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.phoneNumber}</TableCell>
                      <TableCell>{row.meals.join(', ')} </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={statisticSpecialMealsStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                total={totalCount}
                onChange={(step) => handlePaginationChange(step)}
              />
            </Box>
          </Box>
        ) : (
          <EmptyTableData />
        )
      ) : (
        <Loader parentClassName={statisticSpecialMealsStyle.loader} size={50} />
      )}
    </Box>
  );
};

export default StatisticSpecialMeals;
